import * as z from 'zod/v4-mini';

export const Command = {
  help: 'help', // runs automatically at startup?
  clock: 'clock',
  info: 'info',
  login: 'login',
  color: 'color',
  clear: 'clear',
  // logout: 'logout',
  boot: 'boot',
  ssh: 'ssh', // goes to an interactive screen -> "select a device"
  chat: 'chat',
  fullscreen: 'fullscreen',
  tv: 'tv',
  // vote, upload, make?
} as const;

export type Command = (typeof Command)[keyof typeof Command];

export const Color = {
  green: 'green',
  red: 'red',
  blue: 'blue',
  cyan: 'cyan',
  orange: 'orange',
  amber: 'amber',
  white: 'white',
} as const;

export type Color = (typeof Color)[keyof typeof Color];

export const Phase = {
  waiting: 'waiting',
  welcome: 'welcome',
  deadline: 'deadline',
  done: 'done',
} as const;

export type Phase = (typeof Phase)[keyof typeof Phase];

export const phases = Object.values(Phase);

export const BroadcastTopic = {
  chat: 'chat',
  world: 'world',
} as const;

export type BroadcastTopic = (typeof BroadcastTopic)[keyof typeof BroadcastTopic];

const worldSchema = z.object({
  messages: z.array(
    z.object({
      author: z.string(),
      text: z.string(),
      time: z.pipe(
        z.string(),
        z.transform((x) => new Date(x)),
      ),
    }),
  ),
  times: z.object({
    waiting: z.pipe(
      z.string(),
      z.transform((x) => new Date(x)),
    ),
    welcome: z.pipe(
      z.string(),
      z.transform((x) => new Date(x)),
    ),
    deadline: z.pipe(
      z.string(),
      z.transform((x) => new Date(x)),
    ),
    done: z.pipe(
      z.string(),
      z.transform((x) => new Date(x)),
    ),
  }),
  bootGame: z.object({
    started: z.boolean(),
    completed: z.boolean(),
    chars: z.string(),
    index: z.number(),
  }),
  sshGame: z.object({
    started: z.boolean(),
    completed: z.boolean(),
    participants: z.record(z.string(), z.number()),
    deadline: z.nullable(
      z.pipe(
        z.string(),
        z.transform((x) => new Date(x)),
      ),
    ),
  }),
  nextPhase: z.enum(Phase),
  commandsUnlocked: z.array(z.enum(Command)),
  nextUserColors: z.array(z.enum(Color)),
  onlineUsers: z.array(z.string()),
});

export type World = z.infer<typeof worldSchema>;

export function parseServerMessage(json: string) {
  return z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('world'), value: worldSchema }),
      z.object({ kind: z.literal('error'), value: z.string() }),
      z.object({ kind: z.literal('clock'), value: z.string() }),
      z.object({ kind: z.literal('welcome'), value: z.string() }),
      z.object({ kind: z.literal('chat'), value: z.string() }),
      z.object({ kind: z.literal('bootgame'), value: z.string() }),
    ])
    .parse(JSON.parse(json));
}

export type ServerMessage = ReturnType<typeof parseServerMessage>;

const userMessageInputSchema = z.discriminatedUnion('command', [
  z.object({ command: z.literal(Command.chat), message: z.string() }),
  z.object({ command: z.literal(Command.clock) }),
  z.object({ command: z.literal(Command.login) }),
  z.object({ command: z.literal(Command.boot) }),
  z.object({ command: z.literal(Command.ssh) }),
]);

export function parseUserMessage(json: string) {
  return z
    .intersection(z.object({ code: z.string(), color: z.enum(Color) }), userMessageInputSchema)
    .parse(JSON.parse(json));
}

export type UserMessageInput = z.infer<typeof userMessageInputSchema>;
